/**
 * Readable validation errors (Oct 2026). A callable that rejects its input sends a plain message plus the list of
 * fields at fault (in the HttpsError `details`), instead of the raw validator output, so a form can mark the field in
 * red with "Field required" and keep the technical text behind an (i).
 *
 * Server: `throw invalidInput(parsed.error)` (functions/src/lib/invalidInput.ts).
 * Web: `inputIssues(e)` / `errorText(e)` (web/src/lib/inputErrors.ts) + `FieldError` / `ErrorNote`.
 */
/** A rejected field written by hand (client-side checks): "Field required" unless a message is given. */
export function fieldIssue(path, message = 'Field required', code = message === 'Field required' ? 'required' : 'invalid') {
    return { path, message, code, detail: `${code}: ${path}` };
}
/** True for a value a person would call "not filled in": nothing, blank text or a number that isn't one. */
export function isBlankValue(v) {
    return v === undefined || v === null || (typeof v === 'string' && v.trim() === '') || (typeof v === 'number' && Number.isNaN(v));
}
function valueAt(input, path) {
    let v = input;
    for (const k of path) {
        if (v === null || typeof v !== 'object')
            return undefined;
        v = v[k];
    }
    return v;
}
/**
 * `input` (optional): the data that was validated. With it, any rule failing on a blank value (an empty date that
 * fails its pattern, an empty choice) reads "Field required" instead of the rule's own message.
 */
export function fieldIssuesFromZod(issues, input) {
    return issues.map((i) => {
        const missing = (i.code === 'invalid_type' && (i.received === 'undefined' || i.received === 'null' || i.received === 'nan'))
            || (input !== undefined && i.code !== 'custom' && isBlankValue(valueAt(input, i.path)));
        const typeInfo = i.code === 'invalid_type' ? ` (expected ${String(i.expected)}, received ${String(i.received)})` : '';
        return {
            path: i.path.map(String).join('.'),
            message: missing ? 'Field required' : i.message,
            code: i.code,
            detail: `${i.code}${typeInfo}: ${i.message}`,
        };
    });
}
/** "targetEmail" → "target email", "lines.2.amount" → "lines 3 amount" (indexes 1-based for people). */
export function fieldLabel(path) {
    if (!path)
        return 'request';
    return path
        .split('.')
        .map((p) => (/^\d+$/.test(p) ? String(Number(p) + 1) : p.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase()))
        .join(' ');
}
/** One-line message for the whole rejection: "Please check: note (Field required)". */
export function invalidInputMessage(fields) {
    if (!fields.length)
        return 'Some of the entered data is not valid.';
    const parts = fields.slice(0, 4).map((f) => `${fieldLabel(f.path)} (${f.message})`);
    const more = fields.length > 4 ? ` and ${fields.length - 4} more` : '';
    return `Please check: ${parts.join(', ')}${more}.`;
}
export function isInvalidInputDetails(v) {
    return !!v && typeof v === 'object' && v.kind === 'invalid-input'
        && Array.isArray(v.fields);
}
/**
 * Raw validator output as a message (what callables sent before this existed, and what a server that is not yet
 * redeployed still sends): a JSON array of issues. Returns null for any other text.
 */
export function issuesFromLegacyMessage(message) {
    const s = message.trim();
    if (!s.startsWith('['))
        return null;
    try {
        const arr = JSON.parse(s);
        if (!Array.isArray(arr) || !arr.length)
            return null;
        const ok = arr.every((i) => i && typeof i === 'object' && typeof i.code === 'string' && Array.isArray(i.path) && typeof i.message === 'string');
        return ok ? fieldIssuesFromZod(arr) : null;
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=inputErrors.js.map